import { Form, Formik, type FormikHelpers } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckboxField } from "@/features/settings/components/checkbox-field";
import { SelectField } from "@/features/settings/components/select-field";
import {
  createNotice,
  type NoticeCategory,
  NOTICE_CATEGORY_OPTIONS,
  type NoticeDefinition,
  type NoticeSeverity,
  NOTICE_SEVERITY_OPTIONS,
  updateNotice,
} from "@/features/reference/api";
import { applyApiErrors } from "@/lib/form-errors";
import { TextareaField } from "./textarea-field";

const schema = Yup.object({
  code: Yup.string().trim().max(15, "Max 15 characters").required("Code is required"),
  title: Yup.string().trim().max(200, "Max 200 characters").required("Title is required"),
  category: Yup.string().required(),
  severity: Yup.string().required(),
  description: Yup.string().trim().required("Description is required"),
  recommendedAction: Yup.string(),
  isActive: Yup.boolean(),
});

interface FormValues {
  code: string;
  title: string;
  category: NoticeCategory;
  severity: NoticeSeverity;
  description: string;
  recommendedAction: string;
  isActive: boolean;
}

interface Props {
  notice?: NoticeDefinition;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function NoticeDialog({ notice, open, onOpenChange, onSaved }: Props) {
  const isEdit = !!notice;

  async function handleSubmit(values: FormValues, { setErrors }: FormikHelpers<FormValues>) {
    try {
      if (isEdit) await updateNotice(notice.id, values);
      else await createNotice(values);
      onSaved();
      onOpenChange(false);
      toast.success(isEdit ? "Notice updated" : "Notice added");
    } catch (err) {
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't save the notice." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit notice definition" : "Add notice definition"}</DialogTitle>
          <DialogDescription>
            Maps an IRS notice code to its meaning, severity and recommended action.
          </DialogDescription>
        </DialogHeader>
        <Formik<FormValues>
          initialValues={{
            code: notice?.code ?? "",
            title: notice?.title ?? "",
            category: notice?.category ?? "GENERAL",
            severity: notice?.severity ?? "INFO",
            description: notice?.description ?? "",
            recommendedAction: notice?.recommendedAction ?? "",
            isActive: notice?.isActive ?? true,
          }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField name="code" label="Code" placeholder="CP2000" className="uppercase" />
                <FormField name="title" label="Title" />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <SelectField name="category" label="Category" options={NOTICE_CATEGORY_OPTIONS} />
                <SelectField name="severity" label="Severity" options={NOTICE_SEVERITY_OPTIONS} />
              </div>
              <TextareaField name="description" label="Description" rows={3} />
              <TextareaField name="recommendedAction" label="Recommended action" rows={3} />
              <CheckboxField name="isActive" label="Active" hint="Uncheck to retire without deleting." />
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : isEdit ? "Save changes" : "Add notice"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
